import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as api from '../services/api';

const CATEGORIES = [
  { key: 'all', label: 'All Officers' },
  { key: 'executive', label: 'Executive' },
  { key: 'director', label: 'Directors' },
  { key: 'company_secretary', label: 'Company Secretary' },
  { key: 'authorized', label: 'Authorized Staff' },
];

// ── Register Officer Modal ──
function RegisterModal({ onClose, onSave, showToast }) {
  const [form, setForm] = useState({
    officer_name: '', officer_title: '', officer_category: 'director', expires_at: '',
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleSave = async () => {
    if (!form.officer_name || !form.officer_title || !file) {
      showToast('Please fill all required fields and upload a signature image'); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('officer_name', form.officer_name);
      fd.append('officer_title', form.officer_title);
      fd.append('officer_category', form.officer_category);
      if (form.expires_at) fd.append('expires_at', form.expires_at);
      fd.append('signature_image', file);
      await api.uploadSignature(fd);
      showToast('Officer signature registered successfully');
      onSave();
      onClose();
    } catch (err) {
      showToast('Registration failed: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">✍️ Register Officer Signature</div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-grid" style={{ padding: '0 4px' }}>
          <div className="form-group full">
            <label>Officer Full Name *</label>
            <input type="text" placeholder="e.g. Akanimo Udofia" value={form.officer_name}
              onChange={e => setForm({ ...form, officer_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Officer Title / Role *</label>
            <input type="text" placeholder="e.g. Managing Director" value={form.officer_title}
              onChange={e => setForm({ ...form, officer_title: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Category *</label>
            <select value={form.officer_category} onChange={e => setForm({ ...form, officer_category: e.target.value })}>
              <option value="executive">Executive</option>
              <option value="director">Director</option>
              <option value="company_secretary">Company Secretary</option>
              <option value="authorized">Authorized Staff</option>
            </select>
          </div>
          <div className="form-group full">
            <label>Signature Image (PNG or JPEG) *</label>
            <input type="file" accept="image/png,image/jpeg"
              style={{ fontSize: 12, padding: 7, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--white)', width: '100%' }}
              onChange={handleFile} />
            {preview && (
              <div style={{ marginTop: 10, padding: 12, background: 'var(--pale)', borderRadius: 8, textAlign: 'center' }}>
                <img src={preview} alt="preview" style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Signature preview</div>
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Expiry Date (optional)</label>
            <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
          </div>
        </div>
        <div className="form-actions">
          <button className="topbar-btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="topbar-btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Registering...' : 'Register Officer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Apply to Doc Modal — PDF.js click-to-place ──
function ApplyModal({ signature, onClose, onApplied, showToast }) {
  const [docName, setDocName]     = useState('');
  const [pdfFile, setPdfFile]     = useState(null);
  const [rendering, setRendering] = useState(false);
  const [rendered, setRendered]   = useState(false);
  const [applying, setApplying]   = useState(false);
  // sigPos: {x, y} as fractions 0–1 of canvas dimensions, null = not placed yet
  const [sigPos, setSigPos]       = useState(null);
  const [dragging, setDragging]   = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [sigSize, setSigSize]     = useState(28); // stamp width as % of canvas, user-adjustable
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pdfDocRef = useRef(null);
  const canvasRef = useRef(null);

  const CLAMP_HW  = sigSize / 2 / 100; // half-width fraction → keep stamp inside canvas
  const CLAMP_HH  = 0.045;

  const sigSrc = signature.signature_base64
    ? 'data:' + (signature.mime_type || 'image/png') + ';base64,' + signature.signature_base64
    : null;

  // Render a specific page to the canvas
  const renderPage = async (pdfDoc, pageNum) => {
    setRendering(true);
    setRendered(false);
    setSigPos(null);
    try {
      const page = await pdfDoc.getPage(pageNum);
      const vp   = page.getViewport({ scale: 1.5 });
      const canvas = canvasRef.current;
      canvas.width  = vp.width;
      canvas.height = vp.height;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      setRendered(true);
    } catch (_err) {
      showToast('Page render failed');
    } finally {
      setRendering(false);
    }
  };

  // Render PDF to canvas when file chosen
  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setPdfFile(f);
    if (!docName) setDocName(f.name.replace(/\.[^/.]+$/, ''));
    setSigPos(null);
    setRendered(false);
    setRendering(true);
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      const buf    = await f.arrayBuffer();
      const pdfDoc = await pdfjsLib.getDocument({ data: buf }).promise;
      pdfDocRef.current = pdfDoc;
      setTotalPages(pdfDoc.numPages);
      setCurrentPage(1);
      await renderPage(pdfDoc, 1);
    } catch (_err) {
      showToast('PDF preview failed — you can still proceed without a preview');
      setRendering(false);
    }
  };

  // Navigate to a different page
  const goToPage = async (pageNum) => {
    if (!pdfDocRef.current || pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
    await renderPage(pdfDocRef.current, pageNum);
  };

  // Click on canvas wrapper to place stamp
  const handleWrapClick = (e) => {
    if (dragging) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.min(Math.max((e.clientX - rect.left) / rect.width,  CLAMP_HW), 1 - CLAMP_HW);
    const y = Math.min(Math.max((e.clientY - rect.top)  / rect.height, CLAMP_HH), 1 - CLAMP_HH);
    setSigPos({ x, y });
  };

  // Mousedown on stamp starts drag
  const handleStampDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setDragging(true);
    setDragStart({ mx: e.clientX, my: e.clientY, px: sigPos.x, py: sigPos.y });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !dragStart || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const dx   = (e.clientX - dragStart.mx) / rect.width;
    const dy   = (e.clientY - dragStart.my) / rect.height;
    setSigPos({
      x: Math.min(Math.max(dragStart.px + dx, CLAMP_HW), 1 - CLAMP_HW),
      y: Math.min(Math.max(dragStart.py + dy, CLAMP_HH), 1 - CLAMP_HH),
    });
  }, [dragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
    setDragStart(null);
  }, []);

  // Apply signature
  const handleApply = async () => {
    if (!docName) { showToast('Please enter a document name'); return; }
    if (!signature || !signature.id) {
      showToast('Signature record missing — please refresh and try again');
      return;
    }
    setApplying(true);
    try {
      if (pdfFile && sigPos) {
        const fd = new FormData();
        fd.append('signature_id',   signature.id);
        fd.append('document_name',  docName);
        fd.append('position_x_pct', sigPos.x.toFixed(5));
        fd.append('position_y_pct', sigPos.y.toFixed(5));
        fd.append('sig_size_pct',   (sigSize / 100).toFixed(4));
        fd.append('page',           String(currentPage - 1));
        fd.append('document',       pdfFile);
        const res  = await api.stampSignedDoc(fd);
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = 'signed_' + docName + '.pdf';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        await api.applySignature({
          signature_id:   signature.id,
          document_name:  docName,
          position_x_pct: sigPos ? sigPos.x : 0.75,
          position_y_pct: sigPos ? sigPos.y : 0.85,
        });
      }
      showToast('Signature of ' + signature.officer_name + ' applied to "' + docName + '"');
      onApplied();
      onClose();
    } catch (err) {
      showToast('Application failed: ' + ((err.response && err.response.data && err.response.data.error) || err.message));
    } finally {
      setApplying(false);
    }
  };

  const stampLabel = sigPos
    ? ((sigPos.x * 100).toFixed(1) + '% left, ' + (sigPos.y * 100).toFixed(1) + '% top')
    : '';

  return (
    <div className="modal-overlay open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 820, width: '94vw' }} onClick={function(e) { e.stopPropagation(); }}>
        <div className="modal-header">
          <div className="modal-title">Apply Signature — {signature.officer_name}</div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Inputs row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <div className="form-group" style={{ flex: 1, margin: 0 }}>
            <label>Document Name *</label>
            <input type="text" placeholder="e.g. Board Resolution — April 2026"
              value={docName} onChange={function(e) { setDocName(e.target.value); }} />
          </div>
          <div className="form-group" style={{ flex: 1, margin: 0 }}>
            <label>Upload PDF Document</label>
            <input type="file" accept=".pdf"
              style={{ fontSize: 12, padding: 7, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--white)' }}
              onChange={handleFile} />
          </div>
        </div>

        {/* Instruction strip */}
        <div style={{ fontSize: 12, color: '#2563eb', background: '#eff6ff', borderRadius: 6, padding: '6px 12px', marginBottom: 10 }}>
          {rendered ? '👆 Click on the document to place the signature stamp. Then drag to fine-tune.'
            : rendering ? '⏳ Rendering PDF preview…'
            : '📄 Upload a PDF above — it renders here so you can click the exact signature location.'}
        </div>

        {/* Outer shell — dark background, flex-centred, captures drag events */}
        <div
          style={{
            width: '100%', height: 480,
            background: rendered ? '#525659' : 'var(--pale)',
            border: '2px solid var(--border)', borderRadius: 8,
            overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Canvas wrapper — position:relative so child overlay uses canvas % coords */}
          <div
            style={{
              position: 'relative',
              display: rendered ? 'inline-block' : 'none',
              maxHeight: '100%',
              cursor: 'crosshair',
              boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            }}
            onClick={handleWrapClick}
          >
            <canvas
              ref={canvasRef}
              style={{ display: 'block', maxWidth: '100%', maxHeight: 476 }}
            />

            {/* Signature stamp overlay — positioned as % of canvas */}
            {sigPos && (
              <div
                onMouseDown={handleStampDown}
                onClick={function(e) { e.stopPropagation(); }}
                style={{
                  position: 'absolute',
                  left:      (sigPos.x * 100) + '%',
                  top:       (sigPos.y * 100) + '%',
                  transform: 'translate(-50%, -50%)',
                  width:     sigSize + '%',
                  minHeight: 36,
                  border:    '2px solid #2563eb',
                  borderRadius: 3,
                  background:   'rgba(255,255,255,0.92)',
                  cursor:       dragging ? 'grabbing' : 'grab',
                  userSelect:   'none',
                  boxSizing:    'border-box',
                  boxShadow:    '0 2px 8px rgba(37,99,235,0.4)',
                  padding:      3,
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                }}
              >
                {sigSrc
                  ? (<img src={sigSrc} alt="sig" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />)
                  : (<span style={{ fontFamily: 'cursive', fontSize: 13, color: '#1a1a2e', textAlign: 'center' }}>
                      {signature.officer_name ? signature.officer_name.split(' ').slice(0, 2).join(' ') : ''}
                    </span>)
                }
                <div style={{
                  position: 'absolute', top: -18, left: 0,
                  fontSize: 9, color: '#2563eb',
                  background: 'white', padding: '1px 5px', borderRadius: 3,
                  whiteSpace: 'nowrap', border: '1px solid #bfdbfe',
                }}>
                  Drag to fine-tune
                </div>
              </div>
            )}

            {/* "Click to place" hint before first click */}
            {!sigPos && (
              <div style={{
                position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.6)', color: 'white', borderRadius: 6,
                padding: '4px 12px', fontSize: 11, pointerEvents: 'none', whiteSpace: 'nowrap',
              }}>
                Click anywhere to place signature
              </div>
            )}
          </div>

          {/* Placeholder — shown when no PDF loaded yet */}
          {!rendered && !rendering && (
            <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
              <div style={{ fontSize: 48 }}>📄</div>
              <div style={{ fontSize: 13, marginTop: 8, fontWeight: 500 }}>Upload a PDF to preview it here</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Then click where you want the signature</div>
            </div>
          )}

          {rendering && (
            <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
              <div style={{ fontSize: 32 }}>⏳</div>
              <div style={{ fontSize: 13, marginTop: 8 }}>Rendering page {currentPage}…</div>
            </div>
          )}
        </div>

        {/* Page navigation — shown for multi-page PDFs */}
        {totalPages > 1 && rendered && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 12, marginTop: 8,
          }}>
            <button
              className="topbar-btn btn-ghost"
              style={{ padding: '4px 14px', fontSize: 12 }}
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              ← Prev
            </button>
            <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="topbar-btn btn-ghost"
              style={{ padding: '4px 14px', fontSize: 12 }}
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next →
            </button>
          </div>
        )}

        {/* Position readout + resize slider */}
        {sigPos && (
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6 }}>
            {'Position: ' + stampLabel}
            {!pdfFile && ' · Upload a PDF to stamp it and download the signed version'}
          </div>
        )}
        {sigPos && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>Signature size:</span>
            <input
              type="range" min="10" max="55" step="1"
              value={sigSize}
              onChange={e => setSigSize(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#2563eb' }}
            />
            <span style={{ fontSize: 11, color: 'var(--muted)', width: 32 }}>{sigSize}%</span>
          </div>
        )}

        <div style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>
          When you click Apply: if a PDF was uploaded the signed PDF downloads immediately. Every application is audit-logged.
        </div>

        <div className="form-actions">
          <button className="topbar-btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="topbar-btn btn-primary" onClick={handleApply} disabled={applying || !docName}>
            {applying ? 'Applying...' : 'Apply Signature'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Signatures Page ──
export default function Signatures({ showToast, currentUser }) {
  const [signatures, setSignatures] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showRegister, setShowRegister] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [sigRes, logRes] = await Promise.all([api.getSignatures(), api.getSignatureLog()]);
      setSignatures(sigRes.data || []);
      setAuditLog(logRes.data || []);
    } catch (err) { showToast('Failed to load signatures'); }
    finally { setLoading(false); }
  }

  const canRegister = ['head_of_legal', 'legal_admin', 'company_secretary', 'it_admin'].includes(currentUser?.role);

  const filtered = activeCategory === 'all'
    ? signatures
    : signatures.filter(s => s.officer_category === activeCategory);

  const getInitials = (name) => (name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const categoryColor = (cat) => ({
    executive: '#1d4ed8', director: '#6d28d9', company_secretary: '#065f46', authorized: '#b45309'
  })[cat] || '#374151';

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div className="section-header">
        <div className="section-title">✍️ Digital Signature Repository</div>
        {canRegister && (
          <button className="topbar-btn btn-primary" onClick={() => setShowRegister(true)}>
            + Register Officer
          </button>
        )}
      </div>

      <div className="alert alert-blue" style={{ marginBottom: 16 }}>
        🔒 Signature stamps are encrypted and access-controlled. Only Legal &amp; Company Secretary staff may apply signatures to documents. All applications are audit-logged.
      </div>

      {/* Category Filter Tabs */}
      <div className="tab-bar" style={{ marginBottom: 20 }}>
        {CATEGORIES.map(c => (
          <div key={c.key} className={`tab ${activeCategory === c.key ? 'active' : ''}`}
            onClick={() => setActiveCategory(c.key)}>
            {c.label}
            <span style={{ marginLeft: 6, fontSize: 10, background: 'rgba(0,0,0,0.1)', borderRadius: 10, padding: '1px 6px' }}>
              {c.key === 'all' ? signatures.length : signatures.filter(s => s.officer_category === c.key).length}
            </span>
          </div>
        ))}
      </div>

      {/* Signature Cards */}
      <div className="sig-grid">
        {filtered.map(sig => {
          const sigSrc = sig.signature_base64
            ? `data:${sig.mime_type || 'image/png'};base64,${sig.signature_base64}`
            : null;
          const color = categoryColor(sig.officer_category);

          return (
            <div className="sig-card" key={sig.id} style={{ position: 'relative' }}>
              {/* Category badge */}
              <div style={{
                position: 'absolute', top: 10, right: 10, fontSize: 9, fontWeight: 700,
                color: color, background: color + '18', padding: '2px 7px', borderRadius: 10, textTransform: 'uppercase',
              }}>
                {sig.officer_category?.replace('_', ' ')}
              </div>

              {/* Avatar */}
              <div style={{
                width: 56, height: 56, borderRadius: '50%', background: color,
                margin: '0 auto 10px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 700,
              }}>
                {getInitials(sig.officer_name)}
              </div>

              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', textAlign: 'center' }}>{sig.officer_name}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, textAlign: 'center' }}>{sig.officer_title}</div>

              {/* Signature image */}
              <div style={{
                margin: '12px auto 4px', background: 'var(--pale)', border: '1px solid var(--border)',
                borderRadius: 8, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', padding: 8,
              }}>
                {sigSrc ? (
                  <img src={sigSrc} alt={`${sig.officer_name} signature`}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontFamily: 'Dancing Script, cursive', fontSize: 22, color: '#1a1a2e' }}>
                    {sig.officer_name?.split(' ')[0]}
                  </span>
                )}
              </div>

              <div style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'center', marginBottom: 10 }}>
                Updated: {new Date(sig.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {sig.officer_category?.replace('_', ' ')}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="sig-btn primary" onClick={() => setApplyTarget(sig)}>Apply to Doc</button>
                <button className="sig-btn ghost" onClick={() => showToast('Audit log: see Signature Audit Trail below')}>Audit Log</button>
              </div>
            </div>
          );
        })}

        {/* Register New Card */}
        {canRegister && (
          <div className="sig-card" style={{
            border: '2px dashed var(--border)', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: 220,
          }} onClick={() => setShowRegister(true)}>
            <div style={{ fontSize: 36, color: 'var(--mid)' }}>+</div>
            <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600, marginTop: 8 }}>Register New Officer</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, textAlign: 'center', padding: '0 16px' }}>
              Upload signature scan &amp; assign role
            </div>
          </div>
        )}

        {filtered.length === 0 && !canRegister && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
            No signatures registered in this category yet.
          </div>
        )}
      </div>

      {/* Audit Trail */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-title">📋 Signature Audit Trail</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Timestamp</th><th>Officer</th><th>Document</th><th>Applied By</th><th>IP Address</th><th>Status</th></tr>
            </thead>
            <tbody>
              {auditLog.map(l => (
                <tr key={l.id}>
                  <td>{new Date(l.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td>{l.officer_name} ({l.officer_title})</td>
                  <td>{l.document_name}</td>
                  <td>{l.applied_by_name}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{l.ip_address || '-'}</td>
                  <td><span className="badge badge-green">Valid</span></td>
                </tr>
              ))}
              {auditLog.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 20 }}>No signature applications recorded yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onSave={loadData}
          showToast={showToast}
        />
      )}
      {applyTarget && (
        <ApplyModal
          signature={applyTarget}
          onClose={() => setApplyTarget(null)}
          onApplied={loadData}
          showToast={showToast}
        />
      )}
    </div>
  );
}
